import { useCallback } from 'react';
import { useDocumentStorageContext } from 'framework/documentStorage';
import { useParams } from 'react-router-dom';
import { ProcessAssetsType, PublishAssetShowType } from '../types';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { DEFAULT_ERROR_MESSAGE, FormFieldsData, FormShowTemplate } from 'framework/form';
import { normalizeStoragePath, normalizeStoragePathWithSlash } from 'domain/shared/documentStorage';
import { safe } from 'framework/api';
import { loadDiffSafe, useDifferenceByLinks } from 'framework/DiffViewer';
import { useProcessAssetsUrl } from '../router';
import { PROCESS_ASSETS_STATUS } from '../constants';
import { ShowPageLayout } from 'framework/ui/layout';
import PublishProcessAssetContent from './PublishProcessAssetContent';
import { useFileDifferenceApi } from 'infrastructure/api';

async function renderPreviewSafe(
  renderPreviewDocumentTemplate: any,
  path: string,
): Promise<{ path: Nullable<string>; hasError: boolean }> {
  if (!renderPreviewDocumentTemplate || !path) {
    return { path: null, hasError: false };
  }

  const [result, hasError] = await safe<{ path: string }>(() =>
    renderPreviewDocumentTemplate({ templatePath: normalizeStoragePath(path) }),
  );

  return {
    path: result?.path || null,
    hasError,
  };
}

const PublishProcessAssetPage = () => {
  const { assetPath } = useParams();
  const { getListUrl } = useProcessAssetsUrl();
  const { addMessageAndRedirect } = useMessageService();
  const { getDocumentByPaths, uploadDocument, searchDocuments, getDocumentToken, renderPreviewDocumentTemplate } =
    useDocumentStorageContext();
  const { getDifference } = useDifferenceByLinks(useFileDifferenceApi);

  const decodedDocumentPath = decodeURIComponent(assetPath!);

  const getShowData = async () => {
    try {
      const [documentList, docErr] = await safe<ProcessAssetsType[]>(
        () => getDocumentByPaths([decodedDocumentPath]) as Promise<ProcessAssetsType[]>,
      );

      const documentData = documentList?.[0];

      if (docErr || !documentData) {
        throw new Error('Document not found');
      }

      const [searchResult] = await safe(() =>
        searchDocuments({
          pageNumber: 1,
          pageSize: 30,
          filter: {
            entityId: { eq: documentData.metadata?.entityId },
            status: { eq: PROCESS_ASSETS_STATUS.PUBLISHED },
          },
        }),
      );

      const latestPublishedDoc = searchResult?.result?.[0];

      const previewOld = await renderPreviewSafe(
        renderPreviewDocumentTemplate,
        latestPublishedDoc?.path || decodedDocumentPath,
      );

      const previewNew = await renderPreviewSafe(renderPreviewDocumentTemplate, decodedDocumentPath);

      const result: PublishAssetShowType = {
        ...documentData,
        documentPath: decodedDocumentPath,
        previewRenderPath: previewNew.path,
        sourceDiff: null,
        renderedDiff: null,
        loadErrors: {
          previewOld: previewOld.hasError,
          previewNew: previewNew.hasError,
          sourceDiff: false,
          renderedDiff: false,
        },
      };

      if (!latestPublishedDoc?.path || !documentData.path) {
        return result;
      }

      // Source diff
      const sourceDiffResult = await loadDiffSafe(
        getDocumentToken,
        getDifference,
        latestPublishedDoc.path,
        documentData.path,
      );

      result.sourceDiff = sourceDiffResult.diff;
      result.loadErrors.sourceDiff = sourceDiffResult.hasError;

      // Rendered diff
      const renderedDiffResult = await loadDiffSafe(getDocumentToken, getDifference, previewOld.path, previewNew.path);

      result.renderedDiff = renderedDiffResult.diff;
      result.loadErrors.renderedDiff = renderedDiffResult.hasError;

      return result;
    } catch {
      return addMessageAndRedirect(new ErrorMessage(DEFAULT_ERROR_MESSAGE), getListUrl());
    }
  };

  const handlePublish = useCallback(
    async ({ path, metadata }: FormFieldsData) => {
      return uploadDocument({
        ...metadata,
        status: PROCESS_ASSETS_STATUS.PUBLISHED,
        paths: [normalizeStoragePathWithSlash(path)],
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <FormShowTemplate
      getDataFunc={getShowData}
      submitFormFunc={handlePublish}
      afterSubmit={{
        redirect: getListUrl(),
        message: 'You are awesome! The asset has been successfully published.',
      }}
      mapDataForForm={({ path, metadata }) => {
        return {
          path,
          metadata,
        };
      }}
      initEditMode
    >
      <ShowPageLayout hasSeparateSections>
        <PublishProcessAssetContent />
      </ShowPageLayout>
    </FormShowTemplate>
  );
};

export default PublishProcessAssetPage;
